import { useRef, useState } from "react";
import { PaginationQuery, PaginationQueryWithFields } from "../types";
import { createLoadOptions } from "../utils/loadOptions";

export type AsyncOptions<T> = {
  defaultOptions: T[];
  loadOptions: (inputValue: string) => Promise<T[]>;
  onMenuOpen: () => void;
  isLoading: boolean;
};

/**
 * Options of an `AsyncSelect`, spread onto it: the default options are loaded lazily when the
 * menu first opens, so hidden or disabled selects never call the API.
 */
const useAsyncOptions = <T extends object>(
  fetcher: (query: PaginationQuery, signal: AbortSignal) => Promise<T[]>,
  query?: PaginationQueryWithFields<T>,
): AsyncOptions<T> => {
  const [defaultOptions, setDefaultOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isLoaded = useRef(false);
  // Separate refs, so typing in the search does not abort the default options request.
  const defaultAborter = useRef(new AbortController());
  const defaultSearchTerm = useRef("");
  const searchAborter = useRef(new AbortController());
  const searchTerm = useRef("");

  const loadDefaultOptions = createLoadOptions<T, keyof T>(defaultAborter, defaultSearchTerm, fetcher, query);
  const loadOptions = createLoadOptions<T, keyof T>(searchAborter, searchTerm, fetcher, query) as (
    inputValue: string,
  ) => Promise<T[]>;

  const onMenuOpen = () => {
    if (isLoaded.current || isLoading) {
      return;
    }

    setIsLoading(true);
    loadDefaultOptions("")
      .then((options) => {
        isLoaded.current = true;
        setDefaultOptions(options as T[]);
      })
      // Not marked as loaded on failure, so the next opening retries.
      .catch(() => undefined)
      .finally(() => setIsLoading(false));
  };

  return { defaultOptions, loadOptions, onMenuOpen, isLoading };
};

export default useAsyncOptions;
